import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import AvisoDeCicloForaDaTela from 'components/ui/aviso-de-ciclo-fora-da-tela';
import Button from 'components/ui/button';
import CartoesCarregando from 'components/ui/cartoes-carregando';
import PageHeader from 'components/ui/page-header';
import RotuloDeFiltro from 'components/ui/rotulo-de-filtro';
import SeletorDeCiclo from 'components/ui/seletor-de-ciclo';
import SeletorDeMes, { TODOS_OS_MESES } from 'components/ui/seletor-de-mes';
import { useAuth } from 'context/AuthContext';
import { useCicloDaTela } from 'hooks/useCicloDaTela';
import { useServerTime } from 'hooks/useServerTime';
import CalendarioDoCiclo from 'pages/MinhaEquipe/components/CalendarioDoCiclo';
import IlustracaoDestaque from 'assets/img/destaque-ilustracao.png';
import { textoDaContagem } from 'utils/strings';

import {
  AcaoEcoa,
  acaoEditavel,
  acoesDaFase,
  CicloEcoa,
  ciclosDaEcoa,
  contadoresDoSemestre,
  estadoDoCadastro,
  faseDaAcao,
  faseMensalDaAcao,
  podeCadastrarAcao,
  separarAcoes,
} from './acoes-do-semestre';
import CartaoDaMinhaAcao from './components/CartaoDaMinhaAcao';
import CartaoDeParticipacaoEcoa from './components/CartaoDeParticipacaoEcoa';
import PainelDaDinamica from './components/PainelDaDinamica';
import { etapasEcoaDoCiclo, LEGENDA_DO_CALENDARIO_ECOA } from './etapas-ecoa';
import { useAcoesDoSemestre } from './useAcoesDoSemestre';

const RAIZ_DA_PAGINA = /^\/acao-ecoa\/?$/;

type Vista = 'minhas' | 'participacoes';

/** AJ3-28: as duas seções da lista única, nesta ordem. */
const SECOES: { vista: Vista; titulo: string }[] = [
  { vista: 'minhas', titulo: 'Minhas ações' },
  { vista: 'participacoes', titulo: 'Ações que eu participo' },
];

/** ECN-18: textos do vazio de cada lista. */
const VAZIO: Record<Vista, { titulo: string; texto: string }> = {
  minhas: {
    titulo: 'Você não cadastrou Ações ECOA neste ciclo',
    texto: 'Quando você cadastrar uma Ação ECOA, ela aparece aqui para você acompanhar a ciência, a validação e a bonificação.',
  },
  participacoes: {
    titulo: 'Nenhuma participação registrada neste ciclo',
    texto: 'Quando outro integrante da sua Ecoa incluir você numa Ação ECOA, ela aparece aqui.',
  },
};

const INTRODUCAO_DAS_PARTICIPACOES = 'Ações ECOA em que outros integrantes da sua Ecoa incluíram você neste ciclo.';

/** Ações ECOA do integrante: o ciclo inteiro numa leitura, sem seletor de fase (ECN-01 a ECN-28). */
export default function AcoesEcoa() {
  const { user, userInfo, cyclesExtended } = useAuth();
  const location = useLocation();
  const chave: string | undefined = user?._id;

  const ciclos = useMemo(() => (cyclesExtended ?? []) as unknown as CicloEcoa[], [cyclesExtended]);
  const ciclosDoSeletor = useMemo(() => ciclosDaEcoa(ciclos), [ciclos]);
  // CIC-10 a CIC-12 (no lugar de ECN-03 antigo): o ciclo da página é o ciclo do app, no recorte da ECOA (desde o
  // 2S2024); trocar aqui troca o do app.
  const { ciclo, foraDaTela, maisRecente, trocar } = useCicloDaTela(ciclosDoSeletor);
  const cicloId = ciclo?._id;

  const { acoes, carregando, erro, recarregar } = useAcoesDoSemestre(chave, ciclo);
  const { realDatetime: agora, hasTime } = useServerTime();

  // AJ3-32: o mês é do ciclo. Trocar de ciclo por qualquer caminho (o seletor desta página, o do menu, o aviso de ciclo
  // fora da tela) volta a "Todos os meses": o filtro guarda o ciclo em que foi escolhido e é zerado no render em que
  // o ciclo muda, sem depender de quem trocou. Voltar ao ciclo anterior não traz o mês de volta.
  const [mesDoCiclo, setMesDoCiclo] = useState({ cicloId, faseId: TODOS_OS_MESES, escolhido: false });
  if (mesDoCiclo.cicloId !== cicloId) setMesDoCiclo({ cicloId, faseId: TODOS_OS_MESES, escolhido: false });
  const doCiclo = mesDoCiclo.cicloId === cicloId;
  const faseId = doCiclo ? mesDoCiclo.faseId : TODOS_OS_MESES;
  const mesEscolhido = doCiclo && mesDoCiclo.escolhido;
  const [aviso, setAviso] = useState('');

  // ECN-19: ao fechar o cadastro ou a edição (`:id` no Outlet), a página volta à raiz, relê o ciclo e devolve o foco.
  const naRaiz = RAIZ_DA_PAGINA.test(location.pathname);
  const estavaNaRaiz = useRef(naRaiz);
  const paginaRef = useRef<HTMLDivElement>(null);
  const tituloRef = useRef<HTMLHeadingElement>(null);
  const focarTopo = useCallback(() => {
    const botaoDoCadastro = paginaRef.current?.querySelector<HTMLElement>('[data-abre-cadastro]');
    (botaoDoCadastro ?? tituloRef.current)?.focus();
  }, []);
  useEffect(() => {
    if (naRaiz && !estavaNaRaiz.current) {
      recarregar();
      focarTopo();
    }
    estavaNaRaiz.current = naRaiz;
  }, [naRaiz, recarregar, focarTopo]);

  const { minhas, participacoes } = useMemo(() => separarAcoes(acoes as AcaoEcoa[], chave ?? ''), [acoes, chave]);
  const contadores = useMemo(() => contadoresDoSemestre(acoes, chave ?? ''), [acoes, chave]);
  const fases = ciclo?.steps;
  const esperandoLista = (carregando && !acoes.length) || !userInfo;

  const estado = hasTime
    ? estadoDoCadastro({ ciclos, cicloSelecionadoId: cicloId, agora, podeCadastrar: podeCadastrarAcao(userInfo) })
    : null;

  function trocarDeCiclo(id: string) {
    setAviso('');
    trocar(id);
  }

  function escolherMes(id: string) {
    setMesDoCiclo({ cicloId, faseId: id, escolhido: true });
  }

  function concluida(texto: string) {
    setAviso(texto);
    recarregar();
  }

  const listas: Record<Vista, AcaoEcoa[]> = {
    minhas: acoesDaFase(minhas, faseId, fases),
    participacoes: acoesDaFase(participacoes, faseId, fases),
  };

  // O filtro troca a lista sem recarregar a página: quem usa leitor de tela ouve quantas ações ficaram.
  const nomeDoMes = faseId === TODOS_OS_MESES ? 'todos os meses' : fases?.find(fase => fase._id === faseId)?.title;
  const anuncioDoMes =
    mesEscolhido && !esperandoLista && !erro
      ? `${textoDaContagem(listas.minhas.length + listas.participacoes.length, 'ação', 'ações')} em ${nomeDoMes}`
      : '';

  function renderizarCartao(acao: AcaoEcoa, vista: Vista) {
    const fase = faseMensalDaAcao(acao.created, fases);
    const rotuloDaFase = faseDaAcao(acao.created, fases);

    if (vista === 'participacoes') {
      return (
        <CartaoDeParticipacaoEcoa
          key={acao._id}
          data={acao}
          fase={fase}
          rotuloDaFase={rotuloDaFase}
          playerInfo={userInfo}
          onConcluida={concluida}
          onRecarregar={recarregar}
        />
      );
    }

    return (
      <CartaoDaMinhaAcao
        key={acao._id}
        data={acao}
        fase={fase}
        rotuloDaFase={rotuloDaFase}
        playerInfo={userInfo}
        editavel={acaoEditavel(acao, fase, chave, agora)}
        onConcluida={concluida}
        onRecarregar={recarregar}
      />
    );
  }

  function renderizarSecao(vista: Vista) {
    const lista = listas[vista];

    if (!lista.length) {
      return (
        <div className="flex flex-wrap items-center gap-[22px] rounded-jj-lg border border-dashed border-surface-300 bg-brand-white px-[26px] py-6">
          <img src={IlustracaoDestaque} alt="" className="h-auto w-[118px] shrink-0" />
          <div className="flex min-w-[230px] flex-1 flex-col gap-2">
            <p className="m-0 font-display text-[15px] font-bold text-brand-blue-dark">{VAZIO[vista].titulo}</p>
            <p className="m-0 max-w-[440px] text-[12.5px] leading-[1.6] text-content-soft">{VAZIO[vista].texto}</p>
          </div>
        </div>
      );
    }

    if (vista === 'participacoes') {
      return (
        <div className="flex flex-col gap-3 rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
          <p className="m-0 text-xs text-content-soft">{INTRODUCAO_DAS_PARTICIPACOES}</p>
          {lista.map(acao => renderizarCartao(acao, vista))}
        </div>
      );
    }

    return <div className="flex flex-col gap-3">{lista.map(acao => renderizarCartao(acao, vista))}</div>;
  }

  function renderizarLista() {
    if (foraDaTela) return <AvisoDeCicloForaDaTela ciclo={foraDaTela} disponivel={maisRecente} onVer={trocarDeCiclo} />;
    if (erro) {
      return (
        <div className="flex flex-wrap items-center gap-4 rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
          <p className="m-0 flex-1 text-[13px] text-content">Não foi possível carregar as Ações ECOA deste ciclo.</p>
          <Button variant="solido" onClick={recarregar}>
            Tentar novamente
          </Button>
        </div>
      );
    }

    if (esperandoLista) return <CartoesCarregando />;

    // AJ3-28 a AJ3-30: uma lista só, com as duas seções; cada uma tem o próprio vazio.
    return (
      <div className="flex flex-col gap-7">
        {SECOES.map(({ vista, titulo }) => (
          <section key={vista} aria-labelledby={`titulo-ecoa-${vista}`} className="flex flex-col gap-3.5">
            <h2 id={`titulo-ecoa-${vista}`} className="m-0 font-display text-[17px] font-bold leading-[1.2] text-brand-blue-dark">
              {titulo}
            </h2>
            {renderizarSecao(vista)}
          </section>
        ))}
      </div>
    );
  }

  return (
    <>
      <div ref={paginaRef} className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
        <PageHeader ref={tituloRef} titulo="Ações ECOA" voltar={{ to: '/', destino: 'a página inicial' }} />

        <PainelDaDinamica estado={estado} agora={agora} onIrParaCicloAtual={trocarDeCiclo} />

        <div className="mt-3.5 flex flex-wrap items-end gap-[18px] rounded-jj-panel bg-brand-white px-[22px] pb-4 pt-[18px] shadow-jj-01">
          <div className="min-w-[200px] flex-[0_1_240px]">
            <RotuloDeFiltro htmlFor="ecoa-ciclo">Ciclo</RotuloDeFiltro>
            <SeletorDeCiclo ciclos={ciclosDoSeletor} valor={cicloId} onChange={trocarDeCiclo} rotulo="Ciclo" inputId="ecoa-ciclo" />
          </div>

          <div className="min-w-[200px] flex-[0_1_240px]">
            <RotuloDeFiltro htmlFor="ecoa-mes">Mês</RotuloDeFiltro>
            <SeletorDeMes fases={fases} valor={faseId} onChange={escolherMes} rotulo="Mês" inputId="ecoa-mes" />
            <p role="status" className="sr-only">
              {anuncioDoMes}
            </p>
          </div>
        </div>

        <div>
          <dl aria-label="Ações ECOA do ciclo" className="m-0 mt-3.5 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            {[
              { rotulo: 'Cadastradas por você', valor: contadores.cadastradas },
              { rotulo: 'Participações', valor: contadores.participacoes },
              { rotulo: 'Aguardando validação', valor: contadores.aguardando },
            ].map(item => (
              <div key={item.rotulo} className="flex flex-col-reverse justify-end gap-1.5 rounded-jj-lg bg-brand-white px-[18px] py-4 shadow-jj-01">
                <dt className="text-xs leading-[1.35] text-content-soft">{item.rotulo}</dt>
                <dd className="m-0 font-display text-2xl font-bold text-brand-blue">{item.valor}</dd>
              </div>
            ))}
          </dl>
          {/* Sempre montada: anuncia o desfecho de excluir e sair (ECN-63). */}
          <p role="status" className="m-0 mt-3.5 text-xs font-bold text-brand-blue-dark empty:mt-0">
            {aviso}
          </p>
          <div className="mt-3.5">{renderizarLista()}</div>
        </div>

        {ciclo?.status === 'open' && hasTime && (
          <CalendarioDoCiclo
            etapas={etapasEcoaDoCiclo(fases)}
            agora={agora}
            legenda={LEGENDA_DO_CALENDARIO_ECOA}
            titulo="Calendário do semestre"
            subtitulo={`Ciclo ${ciclo.title} · cadastramento, ciência e validação de cada mês`}
          />
        )}
      </div>

      <Outlet />
    </>
  );
}
